import React, { FunctionComponent, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useLanguage } from "../../i18n/useLanguage";
import { useGetVehicleInfo } from "../../queries/useGetVehicleInfo";
import {
  PrimaryAccessoryPart,
  SearchAccessoriesByVehiclePayload,
} from "../../queries/useSearchAccessoriesByVehicle";
import { Pagination } from "../../ui/Pagination/Pagination";
import { NoData } from "../NoData/NoData";
import { AccessoryTile } from "./AccessoryTile";
import { useAccesssoryCategories } from "./Filters/Category/useAccesssoryCategories";

const placeholder = {
  filteredParts: [null, null, null, null, null, null],
  partList: [],
};

const getAccessorySortOrder = (part: PrimaryAccessoryPart): number =>
  part.accessoryCategories.reduce(
    (acc, item) =>
      Math.min(acc, Number(Object.values(item)[0]?.sortOrder) || Infinity),
    Infinity
  );

type AccessoryListProps = {
  data?: SearchAccessoriesByVehiclePayload;
};

export const AccessoryList: FunctionComponent<AccessoryListProps> = ({
  data = placeholder,
}) => {
  const { t } = useTranslation();
  const { data: info } = useGetVehicleInfo();
  const [language] = useLanguage();
  const [categories] = useAccesssoryCategories();
  const sortedParts = useMemo(
    () =>
      data.filteredParts.sort((a, b) => {
        if (!a || !b) return 0;
        const partA = a.partDetails.primaryPart;
        const partB = b.partDetails.primaryPart;
        const sortOrderA = getAccessorySortOrder(partA);
        const sortOrderB = getAccessorySortOrder(partB);
        const nameA = partA.accessoryName[language].toLowerCase();
        const nameB = partB.accessoryName[language].toLowerCase();
        if (sortOrderA !== sortOrderB && categories.length !== 1) {
          return sortOrderA - sortOrderB;
        }
        return nameA.localeCompare(nameB);
      }),
    [data, categories.join("-"), language]
  );
  return (
    <section className="grid gap-2 md:gap-3 lg:gap-4 xl:gap-6 sm:grid-cols-2 xl:grid-cols-3">
      <Pagination className="sm:col-span-2 xl:col-span-3">
        {sortedParts.map((product, key) => (
          <AccessoryTile
            key={product?.partDetails?.primaryPart?.itemId || key}
            part={product}
            vehicleId={info?.vehicleId}
          />
        ))}
      </Pagination>
      {data.filteredParts.length === 0 && (
        <NoData className="col-span-3">{t("searchResults.noData")}</NoData>
      )}
    </section>
  );
};
